import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { clearAllBusinessDrafts, pruneOtherBusinessDrafts } from "@/lib/businessOnboarding";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadProfile = useCallback(async (uid) => {
    if (!uid) {
      setProfile(null);
      return;
    }
    // Retry on transient errors (e.g. 503) so the app never hangs on a blank spinner.
    for (let attempt = 0; attempt < 4; attempt++) {
      const { data, error } = await supabase.from("profiles").select("*").eq("id", uid).maybeSingle();
      if (!error) {
        setProfile(data || null);
        return;
      }
      await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
    }
  }, []);

  useEffect(() => {
    let active = true;
    // Flip loading to false as soon as the session is known; hydrate the profile independently.
    supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSession(data.session);
      setLoading(false);
      loadProfile(data.session?.user?.id);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => {
      // SEC-002: al cambiar de cuenta solo sobrevive el borrador del correo con sesión.
      if (_e === "SIGNED_IN" && s?.user?.email) pruneOtherBusinessDrafts(s.user.email);
      setSession(s);
      setLoading(false);
      loadProfile(s?.user?.id);
    });
    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, [loadProfile]);

  const value = {
    session,
    user: session?.user || null,
    profile,
    role: profile?.role || null,
    loading,
    refreshProfile: () => loadProfile(session?.user?.id),
    signIn: (email, password) => supabase.auth.signInWithPassword({ email, password }),
    signUp: (email, password, meta) =>
      supabase.auth.signUp({ email, password, options: { data: meta } }),
    signOut: async () => {
      clearAllBusinessDrafts(); // SEC-002: los datos temporales del negocio no sobreviven al cierre de sesión
      await supabase.auth.signOut();
      setProfile(null);
    },
    resetPassword: (email) =>
      supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      }),
    updatePassword: (password) => supabase.auth.updateUser({ password }),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
